import React, { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { setModal, selectModal } from "../../../redux/modalSlice";
import { selectDropdowns, selectDropdownOptions } from '../../../redux/dropdownSlice'
import { useForm, Controller } from 'react-hook-form'
import LoxSelect from '../../../components/LoxSelect'
import { reactSelectClassNames } from '../../../helpers/react-select-styles'
import { useRequest } from '../../../hooks/UseRequest'
import { setNotification } from '../../../redux/notificationSlice'
import { selectPaymentScheduleData } from "../../../redux/paymentScheduleDataSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import SpecialInstruction from '../../../components/elements/SpecialInstruction'
import ViewSensitivePiiButtons from "../../../components/ViewSensitivePiiButtons";
import { hasUserPermission } from "../../../utils/permissionHelper";
import { selectUser } from "../../../redux/userSlice";
import { useNavigate } from "react-router-dom";
import { selectPortfolios } from "../../../redux/portfolioSlice";
import Loading from "../../../components/Loading";
import { triggerRerender } from '../../../redux/rerenderSlice';

export default function IdentityTheftModal() {
  const dispatch = useDispatch();
  const modal = useSelector(selectModal);
  const dropdowns = useSelector(selectDropdowns);
  const dropdownOptions = useSelector(selectDropdownOptions);
  const paymentScheduleData = useSelector(selectPaymentScheduleData);
  const active = useSelector(selectUser)
  const portfolios = useSelector(selectPortfolios)

  const navi = useNavigate();

  const identityTheftStatus = dropdowns.data.filter((o) => o.code_key === 'identity_theft_status')[0];
  const identityTheftStatusOptions = dropdownOptions.filter((o) => o.dropdown_id === identityTheftStatus.id).map((o) => ({ label: o.name, value: o.id, codeKey: o.code_key }));

  const [saving, setSaving] = useState(false)
  const request = useRequest()
  const loanId = (modal.options?.latest_loan_id) ?? paymentScheduleData.loanId
  const [files, setFiles] = useState({});
  const [activePermissions, setActivePermissions] = useState([]);
  const [identityTheftStatusType, setidentityTheftStatusType] = useState('');
  const fileType = useRef('')
  const fileInputRef1 = useRef(null)
  const fileInputRef2 = useRef(null)
  const [affidavitRequired, setAffidavitRequired] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [identityTheftData, setIdentityTheftData] = useState(null)
  const { register, handleSubmit, control, watch, setValue, formState: { errors }, reset } = useForm();

  const isOpen = modal.is_active && modal.type === 'identity_theft_modal'
  const inFlightLoanId = useRef(null)

  // Returns everything tied to the loan this modal was opened for to its initial value.
  const resetState = () => {
    setFiles({});
    reset({
      identity_theft_status_dropdown_option_id: null,
      file1: '',
      file2: '',
    });
    if (fileInputRef1.current?.value) {
      fileInputRef1.current.value = '';
    }
    if (fileInputRef2.current?.value) {
      fileInputRef2.current.value = '';
    }
    setidentityTheftStatusType('');
    setIdentityTheftData(null);
    setAffidavitRequired(false);
    setSaving(false);
    setIsLoading(true);
  }

  const handleCancel = () => {
    resetState();
    dispatch(setModal({ is_active: false }));
  };

  const getIdentityTheftStatus = async (forLoanId) => {
    inFlightLoanId.current = forLoanId
    request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/loan/${forLoanId}/identity_fraud_status`)
    try {
      const response = await request.get();
      // Ignore a response that a close or a newer opening has superseded.
      if (inFlightLoanId.current !== forLoanId) return
      setIdentityTheftData(response?.data?.data ?? null);
    } catch (error) {
      if (inFlightLoanId.current !== forLoanId) return
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
    } finally {
      if (inFlightLoanId.current === forLoanId) setIsLoading(false);
    }
  }

  // This modal stays mounted in the Footer, so clear the previous loan's status, files and
  // affidavit requirement on every opening — whatever path closed it last time — before loading.
  useEffect(() => {
    if (isOpen && loanId) {
      resetState();
      getIdentityTheftStatus(loanId);
    } else {
      inFlightLoanId.current = null
    }
  }, [isOpen, loanId]);

  const permissionLookup = {
    identity_theft_status_suspected: 'quick_action_id_theft_suspect_update',
    identity_theft_status_confirmed: 'quick_action_id_theft_confirm_update',
    identity_theft_status_none: 'quick_action_id_theft_none_update'
  }

  const documentKeyLookup = {
    'Police Report': 'identity_theft_police_report',
    Affidavit: 'identity_theft_affidavit'
  }

  useEffect(() => {
    if (modal.options?.latest_loan_id && modal.type === 'identity_theft_modal') {
      setActivePermissions(active.spoofed_user.user_permission_options ? active.spoofed_user.user_permission_options : active.data.user_permission_options)
    }
  }, [modal.options?.latest_loan_id]);

  // Keyed on the record, not its status id, so a loan with the same (or no) status as the previous
  // one still re-syncs instead of keeping the previous loan's selection.
  useEffect(() => {
    if (!identityTheftData) { return }
    const statusOption = identityTheftStatusOptions.find((i) => i.value === identityTheftData.identity_theft_status_dropdown_option_id)
    setidentityTheftStatusType(statusOption?.codeKey ?? '')
    setValue('identity_theft_status_dropdown_option_id', statusOption ?? null)
  }, [identityTheftData])

  useEffect(() => {
    if (!identityTheftData) { return }
    setAffidavitRequired(portfolios.data.find((p) => p.id === identityTheftData.portfolio_id)?.code_key === "LLL");
  }, [identityTheftData])

  const enableButton = () => {
    if (!hasUserPermission(activePermissions, permissionLookup[identityTheftStatusType])) {
      return false;
    }
    if (identityTheftStatusType === "identity_theft_status_confirmed" && affidavitRequired) {
      if (identityTheftData?.identity_theft_police_report_document_id && !identityTheftData?.identity_theft_affidavit_document_id && Object.keys(files).length === 0) {
        return false;
      } else if (!identityTheftData?.identity_theft_police_report_document_id && identityTheftData?.identity_theft_affidavit_document_id && Object.keys(files).length === 0) {
        return false;
      } else if (!identityTheftData?.identity_theft_police_report_document_id && !identityTheftData?.identity_theft_affidavit_document_id && Object.keys(files).length !== 2) {
        return false;
      }
    } else if (identityTheftStatusType === "identity_theft_status_confirmed" && Object.keys(files).length === 0 && !identityTheftData?.identity_theft_police_report_document_id && !identityTheftData?.identity_theft_affidavit_document_id) {
      return false;
    }
    return true;
  }

  const resetData = (isAll = true) => {
    setFiles({});

    reset({
      file2: '',
      file1: '',
    });

    if (fileInputRef1.current?.value) {
      fileInputRef1.current.value = '';
    }
    if (fileInputRef2.current?.value) {
      fileInputRef2.current.value = '';
    }

    if (isAll) {
      reset({
        identity_theft_status_dropdown_option_id: identityTheftData?.identity_theft_status_dropdown_option_id ? identityTheftStatusOptions.find((i) => i.value === identityTheftData.identity_theft_status_dropdown_option_id) : ''
      });
      setidentityTheftStatusType(identityTheftData?.identity_theft_status_dropdown_option_id ? identityTheftStatusOptions.find((i) => i.value === identityTheftData.identity_theft_status_dropdown_option_id)?.codeKey : '');
    }
  }

  const handleIdentityTheftDDOChange = (val) => {
    setidentityTheftStatusType(val?.codeKey);
    resetData(false)
  };

  const getUploadName = (inputId = '1') => {
    switch (identityTheftStatusType) {
      case 'identity_theft_status_confirmed':
      case 'identity_theft_status_suspected':
        fileType.current = (inputId === '1' ? 'document_type_police_report' : 'document_type_affidavit');
        return inputId === '1' ? 'Police Report' : 'Affidavit';
      default:
        return ''
    }
  }

  const onSubmit = async (formData) => {
    if (identityTheftStatusType === 'identity_theft_status_confirmed') {
      if (!enableButton()) {
        dispatch(setNotification({ type: 'error', body: "Please upload the correct number of documents" }))
        return;
      }
    }

      setSaving(true)
      let url = '';
      switch (identityTheftStatusType) {
        case 'identity_theft_status_confirmed':
          url = `${process.env.REACT_APP_API_DOMAIN}/loan/${loanId}/identity_fraud_confirm`
          break;
        case 'identity_theft_status_suspected':
          url = `${process.env.REACT_APP_API_DOMAIN}/loan/${loanId}/identity_fraud_suspect`
          break;
        default:
          url = `${process.env.REACT_APP_API_DOMAIN}/loan/${loanId}/identity_fraud_none`
      }
      request.setEndpoint(url)
      try {
        formData.identity_theft_status_dropdown_option_id = formData.identity_theft_status_dropdown_option_id.value;

        if ('file1' in formData) {
          delete formData.file1;
        }

        if ('file2' in formData) {
          delete formData.file2;
        }

        if (paymentScheduleData?.paymentScheduleCurrentDate !== null) {
          formData.current_date = paymentScheduleData?.paymentScheduleCurrentDate;
        }

        const arrayFiles = Object.values(files)

        const response = await request.modify(formData, arrayFiles)
        if (response.status === 200 || response.status === 201) {
          setSaving(false)
          dispatch(setModal({ is_active: false }));
          dispatch(setNotification({ type: 'success', body: '' }))
          modal.options?.successCallback()
          dispatch(triggerRerender({ subpanelId: 'document' }))
          // reload the page
          const pathSegments = window.location.pathname.split('/');
          const moduleName = pathSegments[1];
          const customerId = modal.options?.customerId
          setTimeout(() => navi(`/${moduleName}/${customerId}`, { state: { reloadPage: true } }), 2000);
        } else {
          setSaving(false)
          resetData();
          dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
        }
      } catch (error) {
        setSaving(false)
        resetData();
        const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
        dispatch(setNotification({ type: 'error', body: errorMessage }))
      }
  };

  return isOpen ? (
    <form onSubmit={handleSubmit(onSubmit)} id="identity_theft_modal_form">
      <div className="modal is-active" data-testid="identity-theft-modal">
        <div className="modal-background"></div>
        <div className="modal-card column is-7">
          <header className="modal-card-head py-3">
            <p className="modal-card-title is-capitalized">
              Identity Theft
            </p>
            <button className="delete" aria-label="close" onClick={handleCancel}></button>
          </header>
          <section
            className="modal-card-body modal-card-body pt-4 pb-1"
            style={{ minHeight: "110px", overflow: 'hidden' }}
          >
            {isLoading ? <Loading /> : (
            <div className="columns mb-0 is-flex-direction-column">
              <div className="columns pt-4 is-flex is-align-content-flex-start">
              <div className="column pt-4">
                <h3>What is the status of the identity theft?</h3>
              </div>
              <div className="column pt-4">
                {activePermissions && dropdownOptions ? (
                  <>
                  <Controller
                    name="identity_theft_status_dropdown_option_id"
                    control={control}
                    defaultValue={identityTheftData?.identity_theft_status_dropdown_option_id ? identityTheftStatusOptions.find((i) => i.value === identityTheftData.identity_theft_status_dropdown_option_id) : ''}
                    rules={{ required: "Please select a reason" }}
                    render={({ field }) => {
                      return (
                        <LoxSelect
                          {...field}
                          testId="identity-theft-modal-status"
                          options={identityTheftStatusOptions}
                          isSearchable={false}
                          isClearable
                          defaultValue={identityTheftData?.identity_theft_status_dropdown_option_id ? identityTheftStatusOptions.find((i) => i.value === identityTheftData.identity_theft_status_dropdown_option_id) : ''}
                          classNames={reactSelectClassNames(errors, 'identity_theft_status_dropdown_option_id')}
                          menuPosition="fixed"
                          styles={{ container: (base) => ({ ...base, width: '100%' }), menu: ({ ...css }) => ({ ...css, width: '100%' }) }}
                          onChange={(val) => {
                            handleIdentityTheftDDOChange(val)
                            setTimeout(() => {
                              field.onChange({ label: val?.label, value: val?.value });
                            }, 0);
                          }}
                          form="identity_theft_modal_form"
                        />
                      )
                    }}
                  />
                  {errors?.identity_theft_status_dropdown_option_id?.message && <p className="has-text-danger">{errors?.identity_theft_status_dropdown_option_id?.message}</p>}
                  </>
                )
                : null }

              </div>
              </div>

              {identityTheftStatusType && identityTheftStatusType !== "identity_theft_status_none" && (
                <>
                {identityTheftStatusType === "identity_theft_status_confirmed" && !affidavitRequired ? <div className="has-text-danger"> One of the following is required: </div> : null }
                  <div className="column pl-5">
                    <h3>Upload {getUploadName()}
                      {(identityTheftStatusType === 'identity_theft_status_confirmed' && affidavitRequired) ?
                        <span className="has-text-danger">*</span> : null}
                      <span
                        style={{ pointerEvents: 'auto' }}
                        className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal`}
                        data-tooltip={"Supported file types: .pdf, .jpg, .jpeg, .png, .doc, .docx"}
                      >
                        <FontAwesomeIcon
                          icon={faCircleInfo}
                          className={`pl-1 has-text-link`}
                        />
                      </span>
                    </h3>
                    {identityTheftData?.[`${documentKeyLookup[getUploadName()]}_document_id`] ? (
                      <div className="mt-1">
                        <ViewSensitivePiiButtons
                          customerId={identityTheftData.customer_id}
                          documentId={identityTheftData[`${documentKeyLookup[getUploadName()]}_document_id`]}
                          documentTypeId={identityTheftData[`${documentKeyLookup[getUploadName()]}_document_type_dropdown_option_id`]}
                          type={'general_document'}
                          noMarginLeft
                          tooltipPosition={'right'}
                        />
                      </div>
                    ) : (
                      <div className="mt-1">
                        <Controller
                          control={control}
                          name="file1"
                          render={({ field: { value, onChange, ...field } }) => {
                            return (
                              <input
                                {...field}
                                value={value?.fileName}
                                title={"No file selected"}
                                onChange={(event) => {
                                  onChange(event.target.files[0]);
                                  getUploadName('1');

                                  const fileObject = {
                                    file: event.target.files[0],
                                    type: fileType.current
                                  }
                                  setFiles({ ...files, [fileType.current]: fileObject });
                                }}
                                type="file"
                                ref={fileInputRef1}
                                data-testid="identity-theft-modal-police-report-upload"
                              />
                            );
                          }}
                        />
                      </div>
                    )}
                  </div>

                  <div className="column pl-5">
                    <h3>Upload {getUploadName('2')}
                      {(identityTheftStatusType === 'identity_theft_status_confirmed' && affidavitRequired) ?
                        <span className="has-text-danger">*</span> : null}
                      <span
                        style={{ pointerEvents: 'auto' }}
                        className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal`}
                        data-tooltip={"Supported file types: .pdf, .jpg, .jpeg, .png, .doc, .docx"}
                      >
                          <FontAwesomeIcon
                            icon={faCircleInfo}
                            className={`pl-1 has-text-link`}
                          />
                        </span>
                      </h3>
                    {identityTheftData?.[`${documentKeyLookup[getUploadName(2)]}_document_id`] ? (
                      <div className="mt-1">
                        <ViewSensitivePiiButtons
                          customerId={identityTheftData.customer_id}
                          documentId={identityTheftData[`${documentKeyLookup[getUploadName(2)]}_document_id`]}
                          documentTypeId={identityTheftData[`${documentKeyLookup[getUploadName(2)]}_document_type_dropdown_option_id`]}
                          type={'general_document'}
                          noMarginLeft
                          tooltipPosition={'right'}
                        />
                      </div>
                      ) : (
                      <div className="mt-1">
                        <Controller
                          control={control}
                          name="file2"
                          render={({ field: { value, onChange, ...field } }) => {
                            return (
                              <input
                                {...field}
                                value={value?.fileName}
                                onChange={(event) => {
                                  onChange(event.target.files[0]);
                                  getUploadName('2');
                                  const fileObject = {
                                    file: event.target.files[0],
                                    type: fileType.current
                                  }
                                  setFiles({ ...files, [fileType.current]: fileObject });
                                }}
                                type="file"
                                ref={fileInputRef2}
                                data-testid="identity-theft-modal-affidavit-upload"
                              />
                            );
                          }}
                        />
                      </div>
                    )}
                    </div>
                </>
              )}
              <div className="column">
                {identityTheftStatusType === "identity_theft_status_confirmed" || identityTheftStatusType === "identity_theft_status_suspected" ? (
                <SpecialInstruction
                  content={"<span>" +
                    "<span class='has-text-weight-bold'>This will perform the following actions:</span><br>" +
                    `Change status from None to ${identityTheftStatusType === 'identity_theft_status_confirmed' ? 'Confirmed' : 'Suspected'}:<br>` +
                    `<span class='pl-2'>• Set loans.identity_theft_status_dropdown_option_id to ${identityTheftStatusType} </span> <br>` +
                    `<span class='pl-2'>• Show ID theft badge (${identityTheftStatusType === "identity_theft_status_confirmed" ? "Confirmed ID Theft" : "Suspected ID Theft"})</span> <br> ` +
                    `<span class='pl-2'>${identityTheftStatusType === "identity_theft_status_confirmed" ? "• Apply DNC and show DNC badge" : ''}</span>` +
                    " </span>"}
                  type="is-warning"
                />
              ) : null}
                {identityTheftStatusType === "identity_theft_status_none" ? (
                  <SpecialInstruction
                    content={"<span>" +
                      "<span class='has-text-weight-bold'>This will perform the following actions:</span><br>" +
                      `Change status from Confirmed/Suspected to None:<br>` +
                      `<span class='pl-2'>• Set loans.identity_theft_status_dropdown_option_id to ${identityTheftStatusType} </span> <br>` +
                      `<span class='pl-2'>• No longer show ID theft badge</span> <br> ` +
                      " </span>"}
                    type="is-warning"
                  />
                ) : null}
              </div>
            </div>
            )}
          </section>
          <footer className="modal-card-foot is-justify-content-end py-1">
            <div className="lox-form__buttons-wrapper has-text-right">
              <button type="button" className="button is-small has-background-grey has-text-white" onClick={handleCancel}>Cancel</button>
              <button
                type="submit"
                data-testid="identity-theft-modal-submit"
                className="button is-danger is-small"
                disabled={(saving || isLoading || !identityTheftStatusType || !enableButton()) && 'disabled'}
              >{saving ? 'Submitting' : 'Update Identity Theft Status'}</button>
            </div>
          </footer>
        </div>
      </div>
    </form>
  ) : null;
}
